import { ModalInjectedProps } from '@sketch/components'

// Utils
import {
  addSharesToCollection,
  addSharesToProject,
  removeSharesFromCollection,
  removeSharesFromProject,
  sortProjectCollections,
} from './utils'

import {
  readShareWithProjectFragment,
  readProjectList,
} from 'modules/projects/operations'

// Hooks
import { useToast } from '@sketch/toasts'

// Types
import {
  CollectionPreviewsFragment,
  ProjectFragment,
  ShareListItemFragment,
  useEditCollectionMutation,
} from '@sketch/gql-types'

interface CurrentShare {
  identifier: string
}

interface EditCollectionProps extends ModalInjectedProps {
  project: ProjectFragment
  collection: CollectionPreviewsFragment
  currentShares: CurrentShare[]
}

export const useEditCollection = ({
  project,
  collection,
  hideModal,
  currentShares,
}: EditCollectionProps) => {
  const { showToast } = useToast()

  const [editCollection, { loading }] = useEditCollectionMutation({
    onCompleted: ({ editCollection }) => {
      const collectioName = editCollection.collection.name

      showToast(`"${collectioName}" changes saved`)
      hideModal()
    },
    onError: 'show-toast',
  })

  const editCollectionWithCacheUpdate: typeof editCollection = (
    options = {}
  ) => {
    const shareIdentifiers: string[] = options.variables?.shareIdentifiers ?? []
    const addAllProjectShares: boolean =
      options.variables?.addAllProjectShares ?? false

    return editCollection({
      ...options,
      update: (cache, { data }) => {
        if (!data) {
          return
        }

        const { collection: updatedCollection } = data.editCollection

        if (collection.name !== updatedCollection.name) {
          sortProjectCollections({
            cache,
            projectIdentifier: project.identifier,
          })
        }

        let sharesToAdd = []

        if (addAllProjectShares) {
          // read the shares directly from the project
          sharesToAdd = readProjectList(cache, {
            projectId: project.identifier,
            ...{ filters: ['NO_COLLECTION'] },
          })?.project.shares.entries.filter(
            maybeShare => maybeShare !== null
          ) as ShareListItemFragment[]
        } else {
          sharesToAdd = shareIdentifiers
            .filter(
              identifier =>
                !currentShares.find(s => s.identifier === identifier)
            )
            .map(identifier => readShareWithProjectFragment(cache, identifier))
            .filter(
              maybeShare => maybeShare !== null
            ) as ShareListItemFragment[]
        }

        addSharesToCollection({
          cache,
          collection,
          projectIdentifier: project.identifier,
          sharesToAdd,
        })

        removeSharesFromProject(cache, project.identifier, sharesToAdd)

        // nothing to remove from collections or add to projects if all shares were selected
        if (!addAllProjectShares) {
          const sharesToRemove = currentShares.filter(
            s =>
              !shareIdentifiers.find(identifier => s.identifier === identifier)
          )

          removeSharesFromCollection({
            cache,
            collectionIdentifier: collection.identifier,
            projectIdentifier: project.identifier,
            sharesToRemove,
          })

          const sharesToAddToProject = sharesToRemove
            .map(s => readShareWithProjectFragment(cache, s.identifier))
            .filter(
              maybeShare => maybeShare !== null
            ) as ShareListItemFragment[]

          addSharesToProject(cache, project.identifier, sharesToAddToProject)
        }
      },
    })
  }

  return {
    editCollection: editCollectionWithCacheUpdate,
    loading,
  }
}
